import { AuditAction } from './schema'

export type DiffKind = 'changed' | 'added' | 'removed'

export interface DiffEntry {
    label: string
    kind: DiffKind
    before?: string
    after?: string
}

type Snapshot = Record<string, unknown> | null

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const formatWeekdays = (days: number[]): string => {
    const sorted = [...days].sort((a, b) => a - b)
    const runs: number[][] = []
    sorted.forEach((d) => {
        const run = runs[runs.length - 1]
        if (run && run[run.length - 1] === d - 1) run.push(d)
        else runs.push([d])
    })
    return runs
        .map((r) =>
            r.length > 2 ? `${WEEKDAYS[r[0]]}–${WEEKDAYS[r[r.length - 1]]}` : r.map((d) => WEEKDAYS[d]).join(', '),
        )
        .join(', ')
}

const formatSlot = (slot: Record<string, unknown>) =>
    slot.endTime ? `${slot.startTime}–${slot.endTime}` : String(slot.startTime)

interface FieldDisplay {
    label: string
    format?: (value: unknown) => string
    formatItem?: (item: unknown) => string
}

// Names arrive as a string, {lang: text} or {key, text: {lang: text}}.
export const formatTranslated = (value: unknown): string => {
    if (typeof value !== 'object' || value === null) return formatValue(value)
    const obj = value as Record<string, unknown>
    if (obj.text && typeof obj.text === 'object') return formatTranslated(obj.text)
    const text = (obj.en ?? Object.values(obj)[0]) as unknown
    return typeof text === 'string' && text ? text : formatValue(value)
}

const FIELD_DISPLAY: Record<string, FieldDisplay> = {
    name: { label: 'Name', format: formatTranslated },
    title: { label: 'Title', format: formatTranslated },
    description: { label: 'Description', format: formatTranslated },
    internalName: { label: 'Internal name' },
    weekdays: { label: 'Days', format: (v) => formatWeekdays(v as number[]) },
    weekday: { label: 'Days', format: (v) => (Array.isArray(v) ? formatWeekdays(v as number[]) : formatValue(v)) },
    showFrom: { label: 'Visible from' },
    showTo: { label: 'Visible until' },
    validFrom: { label: 'Valid from' },
    validTo: { label: 'Valid until' },
    appliesFrom: { label: 'From' },
    appliesTo: { label: 'Until' },
    minTickets: { label: 'Min tickets' },
    maxTickets: { label: 'Max tickets' },
    pricingType: { label: 'Pricing type' },
    businessType: { label: 'Business type' },
    displayType: { label: 'Display type' },
    depositFee: { label: 'Deposit fee' },
    productsListIds: { label: 'Product lists' },
    parentId: { label: 'Parent list' },
    articles: { label: 'Products' },
    externalIds: {
        label: 'External IDs',
        formatItem: (v) => {
            const e = v as Record<string, unknown>
            return `${e.integrationType ?? ''} ${e.externalId ?? ''}`.trim()
        },
    },
    usedResources: {
        label: 'Resources',
        formatItem: (v) => {
            const r = v as Record<string, unknown>
            return `${r.poolId} × ${r.amountUsed}`
        },
    },
    taxConfigurationUuid: { label: 'Tax configuration' },
    reportingCategoryUuid: { label: 'Reporting category' },
    barcodePoolId: { label: 'Barcode pool' },
    reservedArticleId: { label: 'Reserved product' },
    timeSlotsEnabled: { label: 'Time slots' },
    enabledForResellers: { label: 'Resellers' },
    availableFrom: { label: 'From' },
    availableTo: { label: 'Until' },
    priority: { label: 'Priority' },
    visibilityOffsetMinutes: { label: 'Visibility offset (minutes)' },
    productsLists: { label: 'Product lists' },
    article: { label: 'Product' },
    timeSlots: { label: 'Time slots', formatItem: (s) => formatSlot(s as Record<string, unknown>) },
    openingTime: { label: 'Opening time' },
    closingTime: { label: 'Closing time' },
    sellUntilTime: { label: 'Sell until' },
    locationClosed: { label: 'Closed' },
    locationName: { label: 'Location' },
    timezone: { label: 'Timezone' },
}

// Identity and scope, never a user edit.
const IGNORED_KEYS = new Set(['id', 'uuid', 'account', 'accountSlug', 'productId'])

const titleCase = (key: string) => {
    const words = key.replace(/([A-Z])/g, ' $1').toLowerCase()
    return words.charAt(0).toUpperCase() + words.slice(1)
}

export const formatValue = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '—'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (Array.isArray(value)) return value.map(formatValue).join(', ')
    if (typeof value === 'object') {
        const obj = value as Record<string, unknown>
        if (typeof obj.name === 'string' && obj.name) return obj.name
        const { id, uuid, ...rest } = obj
        return JSON.stringify(rest)
    }
    return String(value)
}

const display = (key: string): Required<FieldDisplay> => {
    const d = FIELD_DISPLAY[key] ?? { label: titleCase(key) }
    return { label: d.label, format: d.format ?? formatValue, formatItem: d.formatItem ?? formatValue }
}

const multisetDiff = (before: string[], after: string[]) => {
    const removed = [...before]
    const added: string[] = []
    after.forEach((item) => {
        const i = removed.indexOf(item)
        if (i === -1) added.push(item)
        else removed.splice(i, 1)
    })
    return { added, removed }
}

export function computeDiff(before: Snapshot, after: Snapshot): DiffEntry[] {
    const keys = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])].filter(
        (k) => !IGNORED_KEYS.has(k),
    )
    const entries: DiffEntry[] = []

    keys.forEach((key) => {
        const { label, format, formatItem } = display(key)
        const b = before?.[key]
        const a = after?.[key]
        const bothArrays =
            Array.isArray(b ?? a) && (b === undefined || Array.isArray(b)) && (a === undefined || Array.isArray(a))

        if (bothArrays && !FIELD_DISPLAY[key]?.format) {
            const { added, removed } = multisetDiff(
                ((b as unknown[]) ?? []).map(formatItem),
                ((a as unknown[]) ?? []).map(formatItem),
            )
            removed.forEach((item) => entries.push({ label, kind: 'removed', before: item }))
            added.forEach((item) => entries.push({ label, kind: 'added', after: item }))
            return
        }

        if (before === null) entries.push({ label, kind: 'added', after: format(a) })
        else if (after === null) entries.push({ label, kind: 'removed', before: format(b) })
        else if (format(b) !== format(a)) entries.push({ label, kind: 'changed', before: format(b), after: format(a) })
    })

    return entries
}

export const summarize = (entries: DiffEntry[], action: AuditAction, max = 2): string => {
    if (action !== 'updated') return ''
    const byLabel = new Map<string, DiffEntry[]>()
    entries.forEach((e) => byLabel.set(e.label, [...(byLabel.get(e.label) ?? []), e]))
    const parts = [...byLabel.entries()].map(([label, group]) => {
        if (group[0].kind === 'changed') return `${label} ${group[0].before} → ${group[0].after}`
        const added = group.filter((e) => e.kind === 'added').map((e) => e.after)
        const removed = group.filter((e) => e.kind === 'removed').map((e) => e.before)
        return [label, added.length ? `+${added.join(', ')}` : '', removed.length ? `−${removed.join(', ')}` : '']
            .filter(Boolean)
            .join(' ')
    })
    const rest = parts.length - max
    return parts.slice(0, max).join(' · ') + (rest > 0 ? ` · and ${rest} more` : '')
}
